import {
  PortfolioAllocation,
  PortfolioAllocationDataPoint,
  PortfolioTopHoldingTable,
} from '@models';
import { sortPortfolioDataPoints } from '@products/portfolio/portfolio.utils';
import {
  PortfolioTopHoldingsDTO,
  PortfolioDTO,
  PortfolioComponentId,
  ConfigurationCountry,
  ConfigurationLocale,
  FundId,
} from '@types';
import { formatPercentage } from '@utils/text/number-utils';
import { MapperFactory } from '../mapper-factory';
import {
  getFirstNonEmptyProp,
  hasValues,
  parseStdNumber,
  parseYN,
} from '../mapper.utils';
import { Mapper, MapperParams } from '../type.mapper';
import { TopExposureCalc } from './topexposure.mapper';

export const isTopHolding = (point: PortfolioTopHoldingsDTO): boolean =>
  ![
    TopExposureCalc.LONG,
    TopExposureCalc.SHORT,
    TopExposureCalc.LONG_POSITION,
    TopExposureCalc.SHORT_POSITION,
  ].includes(point.calcnamestd as TopExposureCalc);

// tslint:disable-next-line:max-line-length
// issuers example: https://www.franklintempleton.com/investments/options/exchange-traded-funds/products/26727/SINGLCLASS/franklin-liberty-international-aggregate-bond-etf/FLIA

/**
 * example fund:
 * https://en-us.staging.ftsites.com/investments/options/mutual-funds/products/4402/A/franklin-biotechnology-discovery-fund/FBDIX#portfolio
 */
export class TopHoldingsMapper extends Mapper<PortfolioAllocation[]> {
  constructor(mapperParams: MapperParams, mapperFactory: MapperFactory) {
    super(mapperParams, mapperFactory);
  }

  toDomain(portfolioDto: PortfolioDTO): PortfolioAllocation[] {
    const data: PortfolioTopHoldingsDTO[] = portfolioDto.topholdings;

    // if no data, return null
    if (!data || data.length === 0) {
      return null;
    }

    const filteredData: PortfolioTopHoldingsDTO[] = data.filter(isTopHolding);

    // if no data after filtering, return null
    if (filteredData.length === 0) {
      return null;
    }

    let componentId: PortfolioComponentId =
      PortfolioComponentId.TOP_TEN_HOLDINGS;
    let baseLabelKey = 'top-ten-holdings';

    if (hasValues(data, 'issuername')) {
      componentId = PortfolioComponentId.TOP_TEN_ISSUERS;
      baseLabelKey = 'top-ten-issuers';
    }

    const allocation: PortfolioAllocation = {
      componentId,
      caveatComponentId: PortfolioComponentId.TOP_TEN_HOLDINGS,
      baseLabelKey,
      allocationFlag: getFirstNonEmptyProp(filteredData, 'allocflag'),
      allocationHeading: getFirstNonEmptyProp(
        filteredData,
        'allocheadinglocal'
      ),
      asOfDate: getFirstNonEmptyProp(filteredData, 'asofdate'),
      calculationBasis: getFirstNonEmptyProp(filteredData, 'calcbasislocal'),
      calculationType: getFirstNonEmptyProp(filteredData, 'calctypelocal'),
      hasDerivatives: parseYN(
        getFirstNonEmptyProp(filteredData, 'hasderivatives')
      ),
      updateFrequency: getFirstNonEmptyProp(filteredData, 'frequency'),
      // totalPercent: , // TODO: need PDS to supply this filteredData
      data: filteredData.sort(sortPortfolioDataPoints).map(
        (obj: PortfolioTopHoldingsDTO): PortfolioAllocationDataPoint => ({
          label:
            componentId === PortfolioComponentId.TOP_TEN_ISSUERS
              ? obj.issuername
              : obj.hldngname,
          breakdown: formatPercentage(obj.brkdwnpct),
          breakdownStd: parseStdNumber(obj.brkdwnpctstd),
        })
      ),
    };
    return [allocation];
  }
}

export class TopHoldingsTableMapper extends Mapper<PortfolioTopHoldingTable[]> {
  constructor(mapperParams: MapperParams, mapperFactory: MapperFactory) {
    super(mapperParams, mapperFactory);
  }

  toDomain(
    portfolioTopHoldingsDTO: PortfolioTopHoldingsDTO[]
  ): PortfolioTopHoldingTable[] {
    const portfolioTopHoldingsTable: PortfolioTopHoldingTable[] = [];

    if (Array.isArray(portfolioTopHoldingsDTO)) {
      portfolioTopHoldingsDTO.forEach((portfolioTopHoldingDTO) => {
        const portfolioTopHoldingTable: PortfolioTopHoldingTable = this.createMapper(
          TopHoldingTableMapper
        ).toDomain(portfolioTopHoldingDTO);

        if (portfolioTopHoldingTable) {
          portfolioTopHoldingsTable.push(portfolioTopHoldingTable);
        }
      });
    }

    return portfolioTopHoldingsTable;
  }
}

export class TopHoldingTableMapper extends Mapper<PortfolioTopHoldingTable> {
  constructor(mapperParams: MapperParams, mapperFactory: MapperFactory) {
    super(mapperParams, mapperFactory);
  }

  toDomain(
    portfolioTopHoldingDTO: PortfolioTopHoldingsDTO
  ): PortfolioTopHoldingTable {
    return {
      fundId: portfolioTopHoldingDTO.fundid as FundId,
      countryCode: portfolioTopHoldingDTO.cntrycode as ConfigurationCountry,
      languageCode: portfolioTopHoldingDTO.langcode as ConfigurationLocale,
      asOfDate: portfolioTopHoldingDTO.asofdate,
      asOfDateStd: portfolioTopHoldingDTO.asofdatestd,
      updateFrequency: portfolioTopHoldingDTO.frequency,
      holdingName: portfolioTopHoldingDTO.hldngname,
      geoCode: portfolioTopHoldingDTO.geocode,
      sectorName: portfolioTopHoldingDTO.sectorname,
      breakdownPercentage: portfolioTopHoldingDTO.brkdwnpct,
      breakdownPercentageStd: portfolioTopHoldingDTO.brkdwnpctstd,
      calculationBasis: portfolioTopHoldingDTO.calcbasislocal,
      calculationType: portfolioTopHoldingDTO.calctypelocal,
      allocationFlag: portfolioTopHoldingDTO.allocflag,
      hasDerivatives: parseYN(portfolioTopHoldingDTO.hasderivatives),
    };
  }
}
